import { cloudConfigured, requireSupabase } from "./supabase";
import { useOutletContext } from "react-router-dom";
import type { WarehouseCode } from "../types";

export type ScannerSettings = {
  enabled: boolean;
  model: string;
  networkAddress: string;
  sourceFolder: string;
  dpi: number;
  colorMode: "color" | "grayscale" | "black_white";
  duplex: boolean;
};

export type PrinterSettings = {
  enabled: boolean;
  model: string;
  connection: "bluetooth" | "network" | "usb";
  integration: "print_master" | "android_system" | "network_service";
  labelWidthMm: number;
  labelHeightMm: number;
};

export type ExampleTextSettings = {
  orderNumber: string;
  storageLocation: string;
  supplier: string;
  optional: string;
  returnReason: string;
  scannerModel: string;
  scannerAddress: string;
  scannerFolder: string;
  deviceName: string;
  printerModel: string;
  email: string;
  backupTarget: string;
  departmentCode: string;
  departmentName: string;
  goodsReceiptsSearch: string;
  inventorySearch: string;
  returnsSearch: string;
  pickupsSearch: string;
};

export type DeveloperSettings = {
  schemaVersion: number;
  branding: {
    companyName: string;
    productName: string;
    adminSubtitle: string;
    logoPath: string | null;
    primaryColor: string;
    darkColor: string;
  };
  scanners: Record<WarehouseCode, ScannerSettings>;
  printers: Record<WarehouseCode, PrinterSettings>;
  documents: {
    maxFileSizeMb: number;
    retentionYears: number;
    backupEnabled: boolean;
    backupTarget: string;
    easyTecRequired: boolean;
  };
  workflow: {
    defaultIntake: "scanner" | "camera" | "manual";
    scannerEnabled: boolean;
    cameraEnabled: boolean;
    manualEnabled: boolean;
    pickupSignatureRequired: boolean;
    autoPrintLabels: boolean;
    returnsEnabled: boolean;
  };
  notifications: {
    goodsReceipt: boolean;
    pickup: boolean;
    returnRegistered: boolean;
    dailySummary: boolean;
  };
  support: {
    environmentLabel: string;
    contactEmail: string;
  };
  exampleTexts: ExampleTextSettings;
};

export const defaultDeveloperSettings: DeveloperSettings = {
  schemaVersion: 2,
  branding: {
    companyName: "K&S",
    productName: "Lagersystem",
    adminSubtitle: "Web-Admin",
    logoPath: null,
    primaryColor: "#063F9B",
    darkColor: "#082F72"
  },
  scanners: {
    OB: { enabled: true, model: "", networkAddress: "", sourceFolder: "Farbe", dpi: 300, colorMode: "color", duplex: true },
    MZ: { enabled: true, model: "Brother ADS-2400N", networkAddress: "192.168.178.114", sourceFolder: "Farbe", dpi: 300, colorMode: "color", duplex: true }
  },
  printers: {
    OB: { enabled: true, model: "Phomemo M220", connection: "bluetooth", integration: "print_master", labelWidthMm: 60, labelHeightMm: 40 },
    MZ: { enabled: true, model: "Phomemo M220", connection: "bluetooth", integration: "print_master", labelWidthMm: 60, labelHeightMm: 40 }
  },
  documents: { maxFileSizeMb: 15, retentionYears: 8, backupEnabled: false, backupTarget: "", easyTecRequired: true },
  workflow: {
    defaultIntake: "scanner",
    scannerEnabled: true,
    cameraEnabled: true,
    manualEnabled: true,
    pickupSignatureRequired: true,
    autoPrintLabels: false,
    returnsEnabled: true
  },
  notifications: { goodsReceipt: true, pickup: true, returnRegistered: true, dailySummary: false },
  support: { environmentLabel: "Testbetrieb", contactEmail: "" },
  exampleTexts: {
    orderNumber: "z. B. 2009546-004",
    storageLocation: "z. B. D1",
    supplier: "z. B. UniElektro",
    optional: "optional",
    returnReason: "optional, z. B. Falschlieferung",
    scannerModel: "z. B. Brother ADS-2400N",
    scannerAddress: "192.168.178.114 oder Scannername",
    scannerFolder: "Farbe",
    deviceName: "Samsung Lager OB",
    printerModel: "Phomemo M220",
    email: "dispo@firma.de",
    backupTarget: "z. B. Firmen-NAS / Lager-Backup",
    departmentCode: "ELEKTRO",
    departmentName: "Anzeigename",
    goodsReceiptsSearch: "Auftragsnummer oder Lieferant suchen",
    inventorySearch: "Auftrag, Regal oder Abteilung suchen",
    returnsSearch: "Auftrag, Lager oder Notiz suchen",
    pickupsSearch: "Auftrag, Paketcode oder Monteur suchen"
  }
};

function object(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function text(value: unknown, fallback: string) {
  return typeof value === "string" ? value : fallback;
}

function nullableText(value: unknown, fallback: string | null) {
  return value === null ? null : typeof value === "string" ? value : fallback;
}

function flag(value: unknown, fallback: boolean) {
  return typeof value === "boolean" ? value : fallback;
}

function number(value: unknown, fallback: number) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function scanner(value: unknown, fallback: ScannerSettings): ScannerSettings {
  const row = object(value);
  const colorMode = text(row.colorMode, fallback.colorMode);
  return {
    enabled: flag(row.enabled, fallback.enabled),
    model: text(row.model, fallback.model),
    networkAddress: text(row.networkAddress, fallback.networkAddress),
    sourceFolder: text(row.sourceFolder, fallback.sourceFolder),
    dpi: number(row.dpi, fallback.dpi),
    colorMode: (["color", "grayscale", "black_white"].includes(colorMode) ? colorMode : fallback.colorMode) as ScannerSettings["colorMode"],
    duplex: flag(row.duplex, fallback.duplex)
  };
}

function printer(value: unknown, fallback: PrinterSettings): PrinterSettings {
  const row = object(value);
  const connection = text(row.connection, fallback.connection);
  const integration = text(row.integration, fallback.integration);
  return {
    enabled: flag(row.enabled, fallback.enabled),
    model: text(row.model, fallback.model),
    connection: (["bluetooth", "network", "usb"].includes(connection) ? connection : fallback.connection) as PrinterSettings["connection"],
    integration: (["print_master", "android_system", "network_service"].includes(integration) ? integration : fallback.integration) as PrinterSettings["integration"],
    labelWidthMm: number(row.labelWidthMm, fallback.labelWidthMm),
    labelHeightMm: number(row.labelHeightMm, fallback.labelHeightMm)
  };
}

export function normalizeDeveloperSettings(value: unknown): DeveloperSettings {
  const source = object(value);
  const branding = object(source.branding);
  const scanners = object(source.scanners);
  const printers = object(source.printers);
  const documents = object(source.documents);
  const workflow = object(source.workflow);
  const notifications = object(source.notifications);
  const support = object(source.support);
  const exampleTexts = object(source.exampleTexts);
  const defaultIntake = text(workflow.defaultIntake, defaultDeveloperSettings.workflow.defaultIntake);
  return {
    schemaVersion: number(source.schemaVersion, 2),
    branding: {
      companyName: text(branding.companyName, defaultDeveloperSettings.branding.companyName),
      productName: text(branding.productName, defaultDeveloperSettings.branding.productName),
      adminSubtitle: text(branding.adminSubtitle, defaultDeveloperSettings.branding.adminSubtitle),
      logoPath: nullableText(branding.logoPath, defaultDeveloperSettings.branding.logoPath),
      primaryColor: text(branding.primaryColor, defaultDeveloperSettings.branding.primaryColor),
      darkColor: text(branding.darkColor, defaultDeveloperSettings.branding.darkColor)
    },
    scanners: {
      OB: scanner(scanners.OB, defaultDeveloperSettings.scanners.OB),
      MZ: scanner(scanners.MZ, defaultDeveloperSettings.scanners.MZ)
    },
    printers: {
      OB: printer(printers.OB, defaultDeveloperSettings.printers.OB),
      MZ: printer(printers.MZ, defaultDeveloperSettings.printers.MZ)
    },
    documents: {
      maxFileSizeMb: number(documents.maxFileSizeMb, defaultDeveloperSettings.documents.maxFileSizeMb),
      retentionYears: number(documents.retentionYears, defaultDeveloperSettings.documents.retentionYears),
      backupEnabled: flag(documents.backupEnabled, defaultDeveloperSettings.documents.backupEnabled),
      backupTarget: text(documents.backupTarget, defaultDeveloperSettings.documents.backupTarget),
      easyTecRequired: flag(documents.easyTecRequired, defaultDeveloperSettings.documents.easyTecRequired)
    },
    workflow: {
      defaultIntake: (["scanner", "camera", "manual"].includes(defaultIntake) ? defaultIntake : "scanner") as DeveloperSettings["workflow"]["defaultIntake"],
      scannerEnabled: flag(workflow.scannerEnabled, defaultDeveloperSettings.workflow.scannerEnabled),
      cameraEnabled: flag(workflow.cameraEnabled, defaultDeveloperSettings.workflow.cameraEnabled),
      manualEnabled: flag(workflow.manualEnabled, defaultDeveloperSettings.workflow.manualEnabled),
      pickupSignatureRequired: flag(workflow.pickupSignatureRequired, defaultDeveloperSettings.workflow.pickupSignatureRequired),
      autoPrintLabels: flag(workflow.autoPrintLabels, defaultDeveloperSettings.workflow.autoPrintLabels),
      returnsEnabled: flag(workflow.returnsEnabled, defaultDeveloperSettings.workflow.returnsEnabled)
    },
    notifications: {
      goodsReceipt: flag(notifications.goodsReceipt, defaultDeveloperSettings.notifications.goodsReceipt),
      pickup: flag(notifications.pickup, defaultDeveloperSettings.notifications.pickup),
      returnRegistered: flag(notifications.returnRegistered, defaultDeveloperSettings.notifications.returnRegistered),
      dailySummary: flag(notifications.dailySummary, defaultDeveloperSettings.notifications.dailySummary)
    },
    support: {
      environmentLabel: text(support.environmentLabel, defaultDeveloperSettings.support.environmentLabel),
      contactEmail: text(support.contactEmail, defaultDeveloperSettings.support.contactEmail)
    },
    exampleTexts: {
      orderNumber: text(exampleTexts.orderNumber, defaultDeveloperSettings.exampleTexts.orderNumber),
      storageLocation: text(exampleTexts.storageLocation, defaultDeveloperSettings.exampleTexts.storageLocation),
      supplier: text(exampleTexts.supplier, defaultDeveloperSettings.exampleTexts.supplier),
      optional: text(exampleTexts.optional, defaultDeveloperSettings.exampleTexts.optional),
      returnReason: text(exampleTexts.returnReason, defaultDeveloperSettings.exampleTexts.returnReason),
      scannerModel: text(exampleTexts.scannerModel, defaultDeveloperSettings.exampleTexts.scannerModel),
      scannerAddress: text(exampleTexts.scannerAddress, defaultDeveloperSettings.exampleTexts.scannerAddress),
      scannerFolder: text(exampleTexts.scannerFolder, defaultDeveloperSettings.exampleTexts.scannerFolder),
      deviceName: text(exampleTexts.deviceName, defaultDeveloperSettings.exampleTexts.deviceName),
      printerModel: text(exampleTexts.printerModel, defaultDeveloperSettings.exampleTexts.printerModel),
      email: text(exampleTexts.email, defaultDeveloperSettings.exampleTexts.email),
      backupTarget: text(exampleTexts.backupTarget, defaultDeveloperSettings.exampleTexts.backupTarget),
      departmentCode: text(exampleTexts.departmentCode, defaultDeveloperSettings.exampleTexts.departmentCode),
      departmentName: text(exampleTexts.departmentName, defaultDeveloperSettings.exampleTexts.departmentName),
      goodsReceiptsSearch: text(exampleTexts.goodsReceiptsSearch, defaultDeveloperSettings.exampleTexts.goodsReceiptsSearch),
      inventorySearch: text(exampleTexts.inventorySearch, defaultDeveloperSettings.exampleTexts.inventorySearch),
      returnsSearch: text(exampleTexts.returnsSearch, defaultDeveloperSettings.exampleTexts.returnsSearch),
      pickupsSearch: text(exampleTexts.pickupsSearch, defaultDeveloperSettings.exampleTexts.pickupsSearch)
    }
  };
}

export function useDeveloperSettings() {
  return useOutletContext<{ developerSettings: DeveloperSettings }>().developerSettings;
}

export async function loadDeveloperSettings() {
  if (!cloudConfigured) return defaultDeveloperSettings;
  const { data, error } = await requireSupabase().from("app_settings").select("value").eq("key", "developer_config").maybeSingle();
  if (error) throw error;
  return normalizeDeveloperSettings(data?.value);
}

export function brandingAssetUrl(path: string | null) {
  if (!path || !cloudConfigured) return "";
  return requireSupabase().storage.from("branding-assets").getPublicUrl(path).data.publicUrl;
}

export const DEVELOPER_SETTINGS_EVENT = "ks-developer-settings-updated";
